import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Database, RefreshCw, XCircle } from 'lucide-react';
import type { Health } from 'shared';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { Button } from '@/components/ui/button';
import { apiFetch } from '@/lib/api';
import { formatDateTime } from '@/lib/format';

/**
 * หน้าชั่วคราวของ Phase 1 — ยืนยันว่า web ↔ api ↔ database ต่อกันครบวงจร
 * จะถูกแทนที่ด้วยหน้าจริงของผู้เรียน/แอดมินใน Phase ถัดไป
 */
export function HealthPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith('th') ? 'th-TH' : 'en-GB';

  const health = useQuery({
    queryKey: ['health'],
    queryFn: () => apiFetch<Health>('/health'),
    retry: false,
    refetchInterval: 30_000,
  });

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-xl flex-col gap-6 px-5 py-10">
      <header className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-slate-500">{t('app.org')}</p>
          <h1 className="text-2xl font-semibold text-slate-900">{t('app.title')}</h1>
          <p className="mt-1 text-sm text-slate-600">{t('app.subtitle')}</p>
        </div>
        <LanguageSwitcher />
      </header>

      <section
        aria-live="polite"
        className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
      >
        <h2 className="mb-4 text-lg font-semibold text-slate-900">{t('health.title')}</h2>

        {health.isPending && <p className="text-slate-600">{t('health.checking')}</p>}

        {health.isError && (
          <div className="space-y-3">
            <p className="flex items-center gap-2 font-medium text-red-700">
              <XCircle aria-hidden className="h-5 w-5" />
              {t('health.failed')}
            </p>
            <p className="text-sm text-slate-600">{t('health.failedHint')}</p>
            <Button variant="secondary" size="sm" onClick={() => void health.refetch()}>
              <RefreshCw aria-hidden className="h-4 w-4" />
              {t('common.retry')}
            </Button>
          </div>
        )}

        {health.isSuccess && (
          <dl className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-base font-medium text-emerald-700">
              <CheckCircle2 aria-hidden className="h-5 w-5" />
              {t('health.ok')}
            </div>
            <div className="flex justify-between border-t border-slate-100 pt-2">
              <dt className="flex items-center gap-1.5 text-slate-600">
                <Database aria-hidden className="h-4 w-4" />
                {t('health.database')}
              </dt>
              <dd className="font-medium text-slate-900">
                {health.data.database.connected ? t('health.connected') : t('health.disconnected')}
                {health.data.database.latencyMs !== null && ` · ${health.data.database.latencyMs} ms`}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-600">{t('health.serverTime')}</dt>
              <dd className="font-medium text-slate-900">
                {formatDateTime(health.data.serverTime, { locale })}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-600">{t('health.version')}</dt>
              <dd className="font-medium text-slate-900">{health.data.version}</dd>
            </div>
          </dl>
        )}
      </section>
    </main>
  );
}
